/**
 * SD-8.2 (UX-DR15, §C.3): one setback — a slider for exploring, typing for precision. One value,
 * two instruments, neither authoritative: both write the same form field, so they cannot drift.
 *
 * THE EDITOR'S LIFECYCLE, per the §C.3 table:
 *  - OPEN on click, or on Enter/Space while focused — which is why the affordance is a real
 *    `<button>`: tab-reachability and the two keys come from the platform, not from a keydown
 *    handler pretending to be one. The input mounts with the number SELECTED, so typing replaces.
 *  - COMMIT on the checkmark, Enter, or BLUR. Blur commits because losing a typed number to a
 *    stray click is the worse failure. The checkmark and cross take `onPointerDown` preventDefault
 *    so pressing them never blurs the input first — otherwise the blur-commit would race the
 *    cross and make cancel impossible.
 *  - CANCEL on Esc or the cross. The form value is never touched while typing, so cancelling is
 *    simply closing the editor — the pre-edit value (NOT the default) is still there.
 *  - A non-number reverts silently; out of range clamps to 0–48 AND says so (`clampNote`), so
 *    900 becoming 48 is explained rather than mysterious.
 *
 * The unit is RENDERED beside the number, never typed and never parsed. Discoverability is the
 * dotted underline at rest with a pencil on hover — click-to-edit ships undiscoverable more
 * often than not.
 *
 * COMMITTING HERE IS NOT SAVING (UX-DR16). `onCommit` writes the FORM; the save bar owns the
 * workspace. The parent derives "Unsaved changes" from the same `changedFields` diff every other
 * field uses — this component carries no dirty state of its own.
 */
import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  type DesignSettingsField,
  type DesignSettingsValues,
  type EditorStamp,
  fieldBadge,
} from './designSettingsForm';
import {
  SETBACK_MAX_IN,
  SETBACK_MIN_IN,
  clampNote,
  resolveInlineEdit,
} from './inlineNumberEdit';

export function SetbackSliderField({
  id,
  label,
  unit,
  field,
  values,
  stamp,
  disabled,
  onCommit,
}: {
  id: string;
  label: string;
  unit: string;
  field: DesignSettingsField;
  values: DesignSettingsValues;
  stamp: EditorStamp;
  disabled: boolean;
  onCommit: (field: DesignSettingsField, value: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const badge = fieldBadge(field, values, stamp);
  const raw = Number(values[field]);
  const current = Number.isFinite(raw) ? raw : 0;

  const open = () => {
    setDraft(String(current));
    setNote(null);
    setEditing(true);
  };

  const commit = () => {
    const resolution = resolveInlineEdit(draft);
    if (resolution.kind === 'commit') {
      onCommit(field, resolution.value);
      setNote(clampNote(resolution));
    }
    // 'revert' falls through silently: the form was never written during typing, so closing
    // the editor restores the pre-edit value by construction.
    setEditing(false);
  };

  const cancel = () => setEditing(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        <Badge
          variant="outline"
          className={
            badge.kind === 'yours' ? 'text-foreground' : 'text-muted-foreground border-dashed'
          }
        >
          {badge.label}
        </Badge>
      </div>

      {editing ? (
        <div className="flex items-center gap-1">
          <Input
            id={id}
            type="text"
            inputMode="decimal"
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') cancel();
            }}
            onBlur={commit}
            className="h-8 w-20 text-right tabular-nums"
            aria-label={`${label} setback in inches`}
          />
          <span className="text-sm text-muted-foreground">{unit}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onPointerDown={(e) => e.preventDefault()}
            onClick={commit}
            aria-label={`Commit ${label.toLowerCase()} setback`}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onPointerDown={(e) => e.preventDefault()}
            onClick={cancel}
            aria-label={`Cancel editing ${label.toLowerCase()} setback`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <button
          id={id}
          type="button"
          disabled={disabled}
          onClick={open}
          className="group inline-flex items-baseline gap-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label={`Edit ${label.toLowerCase()} setback`}
        >
          <span className="text-sm font-medium tabular-nums underline decoration-dotted underline-offset-4">
            {current}
          </span>
          <span className="text-sm text-muted-foreground">{unit}</span>
          <Pencil className="h-3 w-3 self-center text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
        </button>
      )}

      <Slider
        min={SETBACK_MIN_IN}
        max={SETBACK_MAX_IN}
        step={1}
        value={[Math.min(SETBACK_MAX_IN, Math.max(SETBACK_MIN_IN, current))]}
        disabled={disabled}
        onValueChange={([v]) => {
          setNote(null);
          onCommit(field, v);
        }}
        aria-label={`${label} setback slider`}
      />

      {note && (
        <p role="status" className="text-xs text-muted-foreground">
          {note}
        </p>
      )}
    </div>
  );
}
